import { forwardRef } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import {
  resolveBaseAppearance,
  resolveBaseLayoutAlign,
  resolveBaseLayoutJustify,
  resolveBaseLayoutLength,
} from './theme';
import type { BaseInlineProps } from './types';
import { cn } from './utils';

export const BaseInline = forwardRef<HTMLDivElement, BaseInlineProps>(
  function BaseInline(
    {
      children,
      gap,
      align = 'center',
      justify = 'start',
      wrap = true,
      appearance,
      className,
      classNames,
      style,
      ...inlineProps
    },
    ref,
  ) {
    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    if (typeof wrap !== 'boolean') {
      throw new TypeError('BaseInline wrap must be a boolean.');
    }
    const resolvedGap =
      gap === undefined
        ? undefined
        : resolveBaseLayoutLength('BaseInline', 'gap', gap);

    return (
      <div
        {...componentAttributes}
        {...inlineProps}
        ref={ref}
        data-monkeys-component="inline"
        data-slot="root"
        data-align={align}
        data-justify={justify}
        data-wrap={wrap ? 'wrap' : 'nowrap'}
        className={cn(theme.slots.inlineRoot, classNames?.root, className)}
        style={{
          ...theme.styles.inline,
          gap: resolvedGap,
          alignItems: resolveBaseLayoutAlign('BaseInline', align),
          justifyContent: resolveBaseLayoutJustify('BaseInline', justify),
          flexWrap: wrap ? 'wrap' : 'nowrap',
          ...style,
        }}
      >
        {children}
      </div>
    );
  },
);
